import Button from '@orn/shared-web/components/Button/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { ChevronDownIcon, ChevronUpIcon, DotIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { listedAlertToAlertRow } from '../../utils/alert/alertRow';
import { getAlertsCenterStyledPresentation, TRUNCATED_ROW_LIMIT } from '../../utils/alert/alertsCenterCard';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE } from '../../utils/sidePanelMotion';
import AlertListItem from '../AlertListItem';
import AlertSection from '../AlertSection';
import AlertTypeIcon from '../AlertTypeIcon';

import AlertsCenterEmptyState from './AlertsCenterEmptyState';
import AlertsCenterHeader, { ALERTS_CENTER_TITLE } from './AlertsCenterHeader';

import type { ListedAlert } from '../../utils/alert/alertRow';
import type { AlertsCenterStatus, IAlertsCenterSection } from '../../utils/alert/alertsCenter';
import type { AlertsCenterPresentation, IAlertsCenterMotion } from '../../utils/alert/alertsCenterCard';

export { TRUNCATED_ROW_LIMIT } from '../../utils/alert/alertsCenterCard';
export type { AlertsCenterPresentation } from '../../utils/alert/alertsCenterCard';

type IconComponent = React.ComponentType<{ className?: string }>;

/** The glyph points where the card is about to go: the pill expands, the cards collapse. */
const CHEVRON_ICON_BY_PRESENTATION: Record<AlertsCenterPresentation, IconComponent> = {
  collapsed: ChevronUpIcon,
  truncated: ChevronDownIcon,
  full: ChevronDownIcon,
};

const CARD_CLASS = 'bg-app_light border-secondary shadow-card flex w-full flex-col rounded-2xl border';

/** Content that appears or leaves with a presentation fades rather than pops. */
const FADE_CLASS = 'transition-opacity duration-150 ease-in-out motion-reduce:transition-none';

/**
 * The `inert` attribute in the form the React 18 runtime keeps: it drops
 * `inert={true}` as an unknown boolean, while `@types/react` types it as one.
 */
const INERT_ATTRIBUTE = { inert: '' } as unknown as { inert: boolean };

/**
 * How far into a collapse the pill waits before fading in over the shrinking
 * card, so it lands in the motion's last stretch rather than covering rows
 * that are still sliding.
 */
const PILL_FADE_IN_DELAY_CLASS = 'delay-[350ms]';

/** The pill's copy — "1 alert to review", "5 alerts to review". */
function formatAlertsToReview(count: number): string {
  return `${count} ${count === 1 ? 'alert' : 'alerts'} to review`;
}

interface IAlertsPillProps {
  count: number;
  /** The pill was clicked. Absent on the decorative copy drawn over a moving card. */
  onExpand?: () => void;
  className?: string;
}

const PILL_CLASS = cx(
  'bg-utility-brand-50 border-utility-brand-100 flex h-[34px] w-full items-center gap-2 rounded-lg border px-3 py-2',
  'shadow-[0_4px_4px_-1px_rgba(23,20,18,0.03)]'
);

const AlertsPillContent = ({ count }: Pick<IAlertsPillProps, 'count'>) => (
  <>
    <DotIcon size="sm" aria-hidden="true" className="text-utility-error-500 shrink-0" />
    <span className="text-utility-brand-700 min-w-0 flex-1 truncate text-left text-sm font-medium">
      {formatAlertsToReview(count)}
    </span>
    <ChevronUpIcon aria-hidden="true" className="text-utility-brand-700 size-4.5 shrink-0" />
  </>
);

/** The collapsed pill: one button at rest, a decorative overlay while the card grows out of it or shrinks into it. */
const AlertsPill = ({ count, onExpand, className }: IAlertsPillProps) =>
  onExpand ? (
    <button
      type="button"
      aria-expanded="false"
      onClick={onExpand}
      className={cx(
        PILL_CLASS,
        'outline-focus-ring cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2',
        className
      )}
    >
      <AlertsPillContent count={count} />
    </button>
  ) : (
    <div aria-hidden="true" className={cx(PILL_CLASS, 'pointer-events-none', className)}>
      <AlertsPillContent count={count} />
    </div>
  );

interface IAlertsCenterErrorStateProps {
  onRetry?: () => void;
}

/**
 * Neutral rather than `bg-error-*`: this is a fetch that did not come back, and
 * a red banner would read as something being wrong with the household's money.
 */
const AlertsCenterErrorState = ({ onRetry }: IAlertsCenterErrorStateProps) => (
  <div className="flex flex-col items-center justify-center gap-1 px-6 py-16 text-center">
    <div className="text-primary text-lg font-medium">We couldn’t load your alerts</div>
    <div className="text-tertiary text-sm">Check your connection and try again.</div>
    {onRetry && (
      <Button className="mt-5" size="sm" color="secondary" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

interface IAlertsCenterBaseProps {
  presentation: AlertsCenterPresentation;
  /**
   * The presentation change in flight, or none at rest. While set, the card
   * renders the union of the outgoing and target trees and fades what only one
   * of them shows; the host transitions the card's height around it.
   */
  motion?: IAlertsCenterMotion;
  /**
   * The household's ACTIVE total from the counts read — what the pill and the
   * truncated header show. Not the number of rows fetched.
   */
  activeCount: number;
  /** Groups with rows, already ordered and filtered — see `buildAlertsCenterView`. */
  sections: IAlertsCenterSection[];
  /** Whole-surface state; only the full presentation renders its non-ready states. */
  status: AlertsCenterStatus;
  /** The collapsed pill was clicked. */
  onExpand: () => void;
  /** The truncated card's "View all" was clicked. */
  onViewAll: () => void;
  /** The header chevron was clicked, in the truncated or the full card. */
  onCollapse: () => void;
  /** The header chevron, for a host that moves focus onto it when the full center opens. */
  collapseButtonRef?: React.Ref<HTMLButtonElement>;
  /** Id of the row drawn with the brand border. */
  selectedId?: string | null;
  /** Id of the row showing the mid-turn refusal pill, or none — see `AlertListItem`. */
  blockedNoticeAlertId?: string | null;
  onSelect?: (alert: ListedAlert) => void;
  onDismiss?: (alertId: string) => void;
  /** Retries every failed read. Only reachable while `status` is `error`. */
  onRetry?: () => void;
  /** Rendered below the list — `alertActionSlot` from `useAlertRowAction`. */
  actionSlot?: React.ReactNode;
  /**
   * Reference instant for the rows' relative timestamps. Defaults to now; pass
   * a fixed date to make a story or test deterministic.
   */
  now?: Date;
  className?: string;
}

/**
 * The Notification Center in one of three presentations: a one-line pill
 * ("N alerts to review"), a card with the first {@link TRUNCATED_ROW_LIMIT}
 * need-attention rows and a "View all" button, or the full center — the pinned
 * header over every group, paged independently. Purely presentational —
 * `useComposerAlertsCenter` owns the reads, the counts, the mutations, and which
 * presentation is showing.
 *
 * The truncated and full cards are one tree: the header, the need-attention
 * `AlertSection`, and its first rows keep their tree positions in both (the
 * unlabeled section renders its header slot as null rather than dropping it),
 * so React keeps those nodes instead of remounting them. Nothing renders when
 * there is nothing to show — the pill at a count of zero, the truncated card
 * with no need-attention rows — so the composer sits flush.
 *
 * While `motion` is set the card is what moves, whichever presentations are
 * involved: it wears the full center's skeleton (`h-full`, a clipped body that
 * fills it) so the host's height transition has something to stretch, mounts
 * whatever either side shows, and wears the outgoing side's opacities and
 * spacing at `pre` and the target's from `active` — so "View all", the section
 * labels, and the pending / history groups fade and slide instead of popping.
 * The pill is a separate element: it draws over the moving card's bottom and
 * dissolves as the card grows out of it, or lands over the last stretch of a
 * collapse.
 *
 * The header count is the server-side total floored by the rows on screen,
 * exactly as the section headers are: the counts read zero-fills on failure
 * and lags a poll behind a fresh archive.
 */
const AlertsCenterBase = ({
  presentation,
  motion,
  activeCount,
  sections,
  status,
  onExpand,
  onViewAll,
  onCollapse,
  collapseButtonRef,
  selectedId,
  blockedNoticeAlertId,
  onSelect,
  onDismiss,
  onRetry,
  actionSlot,
  now,
  className,
}: IAlertsCenterBaseProps) => {
  const needAttention = sections.find((section) => section.section === 'needAttention');
  const needAttentionRows = needAttention?.rows ?? [];
  const shownCount = Math.max(activeCount, needAttentionRows.length);

  // At rest the pill needs something to count and the truncated card needs
  // rows; the full center always renders.
  const rendersNothingAtRest = (target: AlertsCenterPresentation): boolean =>
    target === 'collapsed' ? shownCount === 0 : target === 'truncated' && needAttentionRows.length === 0;

  // The card is what animates, so a move to the pill — or to a card that has
  // nothing to show — keeps rendering the tree it is leaving; the target takes
  // over only once the move settles.
  const tree =
    motion && (presentation === 'collapsed' || rendersNothingAtRest(presentation)) ? motion.from : presentation;
  const styledAs = getAlertsCenterStyledPresentation(presentation, motion);

  if (tree === 'collapsed') {
    if (shownCount === 0) {
      return null;
    }

    return <AlertsPill count={shownCount} onExpand={onExpand} className={className} />;
  }

  const isFullTree = tree === 'full';
  const isMoving = motion !== undefined;
  const showsFullContent = isFullTree || motion?.from === 'full';
  const showsViewAll = tree === 'truncated' || motion?.from === 'truncated';
  const isFullContentVisible = styledAs === 'full';
  const isViewAllVisible = styledAs === 'truncated';
  const isPillVisible = styledAs === 'collapsed';
  const showsPillOverlay = isMoving && (presentation === 'collapsed' || motion.from === 'collapsed');

  if (!isFullTree && !isMoving && needAttentionRows.length === 0) {
    return null;
  }

  // The truncated card is the full center's need-attention group cut to its
  // first rows, unlabeled and without its pager. A collapse out of the full
  // center keeps every group mounted while they fade.
  const visibleSections: IAlertsCenterSection[] = showsFullContent
    ? sections
    : needAttention
      ? [
          {
            section: 'needAttention',
            count: needAttention.count,
            rows: needAttentionRows.slice(0, TRUNCATED_ROW_LIMIT),
          },
        ]
      : [];
  const showSections = isFullTree ? status === 'ready' : true;
  const ChevronIcon = CHEVRON_ICON_BY_PRESENTATION[tree];

  const renderRow = (alert: ListedAlert) => {
    const { title, subtitle, timestamp, status: rowStatus, tone } = listedAlertToAlertRow(alert, now);

    return (
      <AlertListItem
        key={alert.id}
        icon={<AlertTypeIcon alert={alert} />}
        title={title}
        subtitle={subtitle}
        timestamp={timestamp}
        status={rowStatus}
        tone={tone}
        selected={alert.id === selectedId}
        showBlockedNotice={alert.id === blockedNoticeAlertId}
        onClick={onSelect ? () => onSelect(alert) : undefined}
        onDismiss={onDismiss ? () => onDismiss(alert.id) : undefined}
      />
    );
  };

  const fullContentFadeClass = cx(FADE_CLASS, isFullContentVisible ? 'opacity-100' : 'opacity-0');

  return (
    <>
      <div
        role="region"
        className={cx(
          CARD_CLASS,
          'pt-4',
          showsViewAll && 'pb-4',
          (isFullTree || isMoving) && 'h-full min-h-0',
          isMoving && 'overflow-hidden',
          className
        )}
        aria-label={ALERTS_CENTER_TITLE}
      >
        <AlertsCenterHeader
          title={isFullTree ? ALERTS_CENTER_TITLE : `Alerts (${shownCount})`}
          icon={ChevronIcon}
          collapseLabel={isFullTree ? 'Collapse notification center' : 'Collapse alerts'}
          onCollapse={onCollapse}
          collapseButtonRef={collapseButtonRef}
        />

        {/* The full center's body scrolls at rest; while moving it clips, so no
            scrollbar flashes mid-growth, and goes inert, so a row that is
            fading or sliding out of the clip cannot take a click or focus. Its
            spacing tweens between the two presentations' at the panel's
            duration. */}
        <div
          {...(isMoving ? INERT_ATTRIBUTE : undefined)}
          style={isMoving ? SIDE_PANEL_TRANSITION_DURATION_STYLE : undefined}
          className={cx(
            'flex min-h-0 flex-1 flex-col px-4',
            isFullContentVisible ? 'gap-6 py-4' : 'gap-2 py-2',
            isMoving
              ? 'overflow-y-hidden transition-[padding,gap] ease-in-out motion-reduce:transition-none'
              : isFullTree && 'overflow-y-auto'
          )}
        >
          {isFullTree && status === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <LoadingIndicator size="md" />
              <div className="text-tertiary text-sm">Loading your alerts…</div>
            </div>
          )}

          {isFullTree && status === 'error' && <AlertsCenterErrorState onRetry={onRetry} />}

          {isFullTree && status === 'empty' && <AlertsCenterEmptyState />}

          {showSections &&
            visibleSections.map(({ section, count, rows, hasMore, isLoadingMore, onLoadMore }) => {
              const alertSection = (
                <AlertSection
                  key={section}
                  section={section}
                  count={count}
                  labeled={showsFullContent}
                  labelRevealed={isFullContentVisible}
                >
                  {rows.map(renderRow)}
                  {hasMore && onLoadMore && (
                    <div
                      aria-hidden={isFullContentVisible ? undefined : true}
                      className={cx('flex justify-center', fullContentFadeClass)}
                    >
                      <Button
                        size="sm"
                        color="tertiary"
                        onClick={onLoadMore}
                        isLoading={isLoadingMore}
                        isDisabled={isLoadingMore}
                      >
                        Show more
                      </Button>
                    </div>
                  )}
                </AlertSection>
              );

              // The need-attention group is the one both cards share, so it
              // stays bare; the groups only the full center shows fade as one.
              return section === 'needAttention' ? (
                alertSection
              ) : (
                <div
                  key={section}
                  aria-hidden={isFullContentVisible ? undefined : true}
                  className={fullContentFadeClass}
                >
                  {alertSection}
                </div>
              );
            })}
        </div>

        {showsViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            {...(isViewAllVisible ? undefined : INERT_ATTRIBUTE)}
            className={cx(
              'border-secondary_alt text-brand-secondary hover:text-brand-secondary_hover outline-focus-ring mx-4 flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2',
              FADE_CLASS,
              isViewAllVisible ? 'opacity-100' : 'opacity-0'
            )}
          >
            View all
          </button>
        )}

        {/* Outside the scroller so a failed reconnect stays put while the list
            scrolls. Its own `mx-4` matches the body's inset. */}
        {isFullTree && actionSlot}
      </div>

      {showsPillOverlay && (
        <AlertsPill
          count={shownCount}
          className={cx(
            'absolute inset-x-0 bottom-0',
            FADE_CLASS,
            isPillVisible ? 'opacity-100' : 'opacity-0',
            isPillVisible && motion.stage === 'active' && PILL_FADE_IN_DELAY_CLASS
          )}
        />
      )}
    </>
  );
};

export default AlertsCenterBase;
